import { useEffect, useMemo, useState } from "preact/hooks";
import { Link, useLocation } from "wouter";
import {
  Customers,
  Invoices,
  Stats,
  type InvoiceListRow,
  type ProjectionSummary,
  type StatsSummary,
} from "../lib/api";
import type { Customer, InvoiceStatus } from "@shared/types";
import { formatMoney, formatDateSk } from "@shared/format";
import { currentYear as getCurrentYear } from "@shared/date";
import { StatusBadge } from "../components/StatusBadge";
import { describeError } from "../lib/errors";

export function Home() {
  const [, setLocation] = useLocation();
  const currentYear = getCurrentYear();

  const [year, setYear] = useState<number | "all">(currentYear);
  const [status, setStatus] = useState<InvoiceStatus | "all">("all");
  const [customerId, setCustomerId] = useState<number | "all">("all");

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [rows, setRows] = useState<InvoiceListRow[] | null>(null);
  const [stats, setStats] = useState<StatsSummary | null>(null);
  const [projection, setProjection] = useState<ProjectionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);

  useEffect(() => {
    Customers.list().then((r) => setCustomers(r.customers)).catch(() => {});
    Stats.summary().then(setStats).catch(() => {});
    Stats.projection(currentYear).then(setProjection).catch(() => {});
  }, [currentYear]);

  useEffect(() => {
    setRows(null);
    setError(null);
    Invoices.list({
      ...(year !== "all" ? { year } : {}),
      ...(status !== "all" ? { status } : {}),
      ...(customerId !== "all" ? { customerId } : {}),
    })
      .then((r) => setRows(r.invoices))
      .catch((e) => setError(describeError(e)));
  }, [year, status, customerId]);

  const years = useMemo(() => {
    const ys = new Set<number>([currentYear]);
    for (const r of rows ?? []) {
      ys.add(Number(r.issue_date.slice(0, 4)));
    }
    return [...ys].sort((a, b) => b - a);
  }, [rows, currentYear]);

  const lastIssued = useMemo(
    () => rows?.find((r) => r.status === "issued") ?? null,
    [rows]
  );

  function slugFor(inv: { id: number; number: string | null }): string {
    return inv.number ?? String(inv.id);
  }

  async function duplicate(slug: string) {
    if (duplicating) return;
    setDuplicating(slug);
    try {
      const r = await Invoices.duplicate(slug);
      setLocation(`/invoices/${slugFor(r.invoice)}/edit`);
    } catch (e) {
      setError(describeError(e));
      setDuplicating(null);
    }
  }

  return (
    <main class="app-shell">
      <section class="hero">
        <div class="hero__cta">
          {lastIssued && lastIssued.number ? (
            <>
              <button
                type="button"
                class="btn primary btn--lg"
                onClick={() => duplicate(lastIssued.number!)}
                disabled={duplicating !== null}
              >
                {duplicating === lastIssued.number
                  ? "Vytváram…"
                  : `Vytvoriť z poslednej (${lastIssued.number})`}
              </button>
              <Link href="/invoices/new" class="btn btn--lg">
                Nová faktúra
              </Link>
            </>
          ) : (
            <Link href="/invoices/new" class="btn primary btn--lg">
              Vytvoriť novú faktúru
            </Link>
          )}
        </div>

        {stats && (
          <div class="stat-strip">
            <Stat
              label={`YTD ${stats.year} bez DPH`}
              value={formatMoney(stats.ytdNet)}
            />
            {projection &&
              (() => {
                const safePace = projection.thresholdNet / 12;
                const pacePct =
                  safePace > 0
                    ? Math.round(
                        (projection.averages.perMonthNet / safePace) * 100
                      )
                    : 0;
                const monthsRemaining = Math.max(
                  0,
                  12 - projection.monthsElapsed
                );
                const requiredPace =
                  monthsRemaining > 0
                    ? Math.max(0, projection.headroom.remainingNet) /
                      monthsRemaining
                    : 0;
                const headroomTone: "ok" | "warn" | "danger" =
                  projection.headroom.utilizationPct >= 95
                    ? "danger"
                    : projection.headroom.utilizationPct >= 80
                      ? "warn"
                      : "ok";
                const paceTone: "ok" | "warn" | "danger" =
                  pacePct >= 100 ? "danger" : pacePct >= 80 ? "warn" : "ok";
                return (
                  <>
                    <Stat
                      label="Do hranice €100k"
                      value={formatMoney(projection.headroom.remainingNet)}
                      tone={headroomTone}
                    />
                    <Stat
                      label="Tempo vs. hranica"
                      value={`${pacePct.toLocaleString("sk-SK")} %`}
                      tone={paceTone}
                    />
                    <Stat
                      label={
                        monthsRemaining > 0
                          ? `Cieľové tempo (${monthsRemaining} mes)`
                          : "Cieľové tempo"
                      }
                      value={
                        monthsRemaining > 0
                          ? `${formatMoney(requiredPace)}/mes`
                          : "—"
                      }
                    />
                  </>
                );
              })()}
            <Stat
              label="Hodiny YTD"
              value={`${stats.ytdHours.toLocaleString("sk-SK")} h`}
            />
          </div>
        )}
      </section>

      <section class="recent">
        <header class="page__head">
          <h2>Faktúry</h2>
        </header>

        <section class="filters">
          <label class="field">
            <span>Rok</span>
            <select
              value={year === "all" ? "all" : String(year)}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                setYear(v === "all" ? "all" : Number(v));
              }}
            >
              <option value="all">Všetky roky</option>
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label class="field">
            <span>Stav</span>
            <select
              value={status}
              onChange={(e) =>
                setStatus(
                  (e.target as HTMLSelectElement).value as InvoiceStatus | "all"
                )
              }
            >
              <option value="all">Všetky stavy</option>
              <option value="draft">Koncept</option>
              <option value="issued">Vystavené</option>
            </select>
          </label>
          <label class="field">
            <span>Odberateľ</span>
            <select
              value={customerId === "all" ? "all" : String(customerId)}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                setCustomerId(v === "all" ? "all" : Number(v));
              }}
            >
              <option value="all">Všetci odberatelia</option>
              {customers.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </section>

        {error && <p class="error">{error}</p>}

        {rows === null ? (
          <p class="muted">Načítavam…</p>
        ) : rows.length === 0 ? (
          <p class="muted">Žiadne faktúry.</p>
        ) : (
          <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>Číslo</th>
                <th>Odberateľ</th>
                <th>Dátum</th>
                <th>Splatnosť</th>
                <th class="num">Suma</th>
                <th>Stav</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => {
                const slug = slugFor(inv);
                return (
                  <tr key={inv.id}>
                    <td>
                      <Link href={`/invoices/${slug}`}>
                        {inv.number ?? <span class="muted">koncept</span>}
                      </Link>
                    </td>
                    <td>{inv.customer.name ?? "—"}</td>
                    <td>{formatDateSk(inv.issue_date)}</td>
                    <td>{formatDateSk(inv.due_date)}</td>
                    <td class="num">{formatMoney(inv.totals.gross)}</td>
                    <td>
                      <StatusBadge status={inv.status} />
                    </td>
                    <td class="row-actions">
                      {inv.status === "issued" && inv.number ? (
                        <button
                          type="button"
                          class="btn btn--sm"
                          disabled={duplicating !== null}
                          onClick={() => duplicate(inv.number!)}
                        >
                          {duplicating === inv.number
                            ? "Duplikujem…"
                            : "Duplikovať"}
                        </button>
                      ) : (
                        <Link href={`/invoices/${slug}/edit`} class="btn btn--sm">
                          Upraviť
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        {rows && rows.length > 0 && (
          <p class="recent__footer muted">
            Zobrazené: {rows.length.toLocaleString("sk-SK")}{" "}
            {rows.length === 1
              ? "faktúra"
              : rows.length >= 2 && rows.length <= 4
                ? "faktúry"
                : "faktúr"}
          </p>
        )}
      </section>
    </main>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "danger";
}) {
  return (
    <div class={tone ? `stat stat--${tone}` : "stat"}>
      <span class="stat__label">{label}</span>
      <span class="stat__value">{value}</span>
    </div>
  );
}
